import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import Toast from 'react-native-root-toast';
import { Feather } from '@expo/vector-icons';

import {
	PEOPLE_EMPLOYEE_DOCUMENT_CATEGORY,
	PEOPLE_EMPLOYEE_EVENT_SELF_REPORT_TYPES,
	PEOPLE_EMPLOYEE_EVENT_TYPE,
	PEOPLE_EMPLOYEE_EVENT_TYPE_NAMES,
	PEOPLE_EMPLOYEE_EVENT_TYPE_PROMPTS,
} from '../../../common/constants';
import { buildShiftTimeOptions } from '../../../common/utils/people/roster';
import { CONFIDENTIAL_EMPLOYEE_EVENT_SUBMISSION_HINT } from '../../../common/utils/people/employeeEventConfidentiality';
import { useAppContext } from '../../../common/utils/context';
import { REPORT_MY_EMPLOYEE_EVENT } from '../../../common/graphql/people/employeeEvent';
import {
	CREATE_EMPLOYEE_DOCUMENT_RECORD_MOBILE,
	GET_MY_EMPLOYEE_PROFILE_MOBILE,
} from '../../graphql/people';
import { uploadPeopleImageAsset } from '../../utils/peopleFileUpload';
import { Picker, SwitchWithLabel, Text } from '../../components';
import { DateSelectorInput, TextInput } from '../../components/input';
import {
	ActionDock,
	Card,
	EmptyState,
	Screen,
	SecondaryButton,
} from '../../design/components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import usePeoplePhotoPicker from './usePeoplePhotoPicker';

const EVENT_TYPE_OPTIONS = PEOPLE_EMPLOYEE_EVENT_SELF_REPORT_TYPES.map(type => ({
	value: type,
	label: PEOPLE_EMPLOYEE_EVENT_TYPE_NAMES[type] || 'Event',
}));

const TIME_OPTIONS = buildShiftTimeOptions({ toMinutes: (24 * 60) - 15 });
const DEFAULT_OCCURRED_MINUTES = 9 * 60;
const getEmployeeDisplayName = (employee) => {
	const parts = [
		employee?.preferredName || employee?.firstName || '',
		employee?.lastName || '',
	].filter(Boolean);

	return parts.join(' ').trim();
};

// Self-service incident report, mirroring the People app My account Report incident tab.
// The report goes to the employee's manager where available; photos are stored as event attachments.
const ReportIncidentView = () => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const { getServerEndpoint } = useAppContext();
	const { pickPhoto } = usePeoplePhotoPicker();

	const { data: profileData, loading: profileLoading } = useQuery(GET_MY_EMPLOYEE_PROFILE_MOBILE, {
		fetchPolicy: 'cache-and-network',
	});
	const employee = profileData?.getMyEmployeeProfile || null;

	const [reportEvent] = useMutation(REPORT_MY_EMPLOYEE_EVENT);
	const [createDocumentRecord] = useMutation(CREATE_EMPLOYEE_DOCUMENT_RECORD_MOBILE);

	const [type, setType] = useState(PEOPLE_EMPLOYEE_EVENT_TYPE.HAZARD);
	const [occurredDate, setOccurredDate] = useState('');
	const [occurredMinutes, setOccurredMinutes] = useState(DEFAULT_OCCURRED_MINUTES);
	const [summary, setSummary] = useState('');
	const [details, setDetails] = useState('');
	const [isConfidential, setIsConfidential] = useState(false);
	const [pendingAssets, setPendingAssets] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const occurredAt = useMemo(() => {
		if (!occurredDate) {
			return null;
		}

		return moment(occurredDate, 'YYYY-MM-DD')
			.startOf('day')
			.add(occurredMinutes, 'minutes')
			.toDate();
	}, [occurredDate, occurredMinutes]);

	const addPhoto = useCallback(async () => {
		const asset = await pickPhoto();
		if (asset) {
			setPendingAssets(previousAssets => [...previousAssets, asset]);
		}
	}, [pickPhoto]);

	const removePhoto = useCallback((indexToRemove) => {
		setPendingAssets(previousAssets => previousAssets.filter((_, index) => index !== indexToRemove));
	}, []);

	const onSubmit = useCallback(async () => {
		if (!employee?.id) {
			return;
		}

		if (!details.trim()) {
			Toast.show('Describe what happened in the details field');
			return;
		}

		setIsSubmitting(true);
		try {
			const attachmentIds = [];

			// Upload photos and register them as event attachment documents, mirroring the
			// People web flow so reports stay auditable.
			for (const asset of pendingAssets) {
				const uploadedFile = await uploadPeopleImageAsset({
					asset,
					employeeId: employee.id,
					getServerEndpoint,
				});

				if (!uploadedFile) {
					Toast.show('Photo upload failed');
					return;
				}

				const response = await createDocumentRecord({
					variables: {
						input: {
							employee: employee.id,
							category: PEOPLE_EMPLOYEE_DOCUMENT_CATEGORY.EVENT_ATTACHMENT,
							title: uploadedFile.fileName,
							files: [{
								key: 'file',
								s3Key: uploadedFile.s3Key,
								mimeType: uploadedFile.mimeType,
								sizeBytes: uploadedFile.sizeBytes,
							}],
						},
					},
				});

				const createdDocumentId = response?.data?.createEmployeeDocumentRecord?.id;
				if (createdDocumentId) {
					attachmentIds.push(createdDocumentId);
				}
			}

			await reportEvent({
				variables: {
					type,
					occurredAt: occurredAt || undefined,
					summary: summary.trim() || undefined,
					details: details.trim(),
					isConfidential,
					attachments: attachmentIds.length ? attachmentIds : undefined,
				},
			});

			Toast.show(isConfidential
				? 'Report submitted confidentially — it will not appear in your history'
				: 'Incident report submitted');
			setType(PEOPLE_EMPLOYEE_EVENT_TYPE.HAZARD);
			setOccurredDate('');
			setOccurredMinutes(DEFAULT_OCCURRED_MINUTES);
			setSummary('');
			setDetails('');
			setIsConfidential(false);
			setPendingAssets([]);
		}
		catch (error) {
			console.error('Incident report submit failed', error);
			Toast.show(error?.message || 'Unable to submit report');
		}
		finally {
			setIsSubmitting(false);
		}
	}, [
		createDocumentRecord,
		details,
		employee?.id,
		getServerEndpoint,
		isConfidential,
		occurredAt,
		pendingAssets,
		reportEvent,
		summary,
		type,
	]);

	if (!employee) {
		return (
			<Screen
				backLabel="My account"
				heading="Report incident"
				isLoading={profileLoading}
			>
				<EmptyState
					icon="user-x"
					title="Employee profile required"
					message="No employee profile is linked to this account. Incident reports are available once you have an employee profile."
				/>
			</Screen>
		);
	}

	const managerName = getEmployeeDisplayName(employee.managerEmployee);
	const notificationText = managerName
		? `Your manager ${managerName} is the intended reviewer for this report. If they cannot be notified, the responsible reviewer will be notified.`
		: 'The responsible reviewer will be notified of this report.';

	return (
		<Screen
			backLabel="My account"
			heading="Report incident"
			subheading={!isConfidential ? notificationText : undefined}
			dock={(
				<ActionDock
					action={{
						label: isSubmitting ? 'Submitting…' : 'Submit report',
						onPress: onSubmit,
						disabled: isSubmitting || !details.trim(),
					}}
				/>
			)}
		>
			<View style={styles.form}>
				<View style={styles.formField}>
					<Text style={styles.formLabel}>Type</Text>
					<Picker
						options={EVENT_TYPE_OPTIONS}
						selectedValue={type}
						onValueChange={setType}
						width="100%"
					/>
					<Text style={styles.mutedText}>{PEOPLE_EMPLOYEE_EVENT_TYPE_PROMPTS[type]}</Text>
				</View>

				<View style={styles.formField}>
					<Text style={styles.formLabel}>Occurred on</Text>
					<DateSelectorInput
						value={occurredDate}
						onChange={setOccurredDate}
						placeholder="Select date (optional)"
					/>
				</View>

				{
					occurredDate ? (
						<View style={styles.formField}>
							<Text style={styles.formLabel}>Around what time</Text>
							<Picker
								options={TIME_OPTIONS}
								selectedValue={occurredMinutes}
								onValueChange={setOccurredMinutes}
								width="100%"
							/>
						</View>
					) : null
				}

				<View style={styles.formField}>
					<Text style={styles.formLabel}>Summary</Text>
					<TextInput
						value={summary}
						onChange={setSummary}
						placeholder="Short summary"
					/>
				</View>

				<View style={styles.formField}>
					<Text style={styles.formLabel}>Details (required)</Text>
					<TextInput
						value={details}
						onChange={setDetails}
						placeholder="Describe what happened, where it happened, and who was involved."
						multiline={true}
					/>
				</View>

				<View style={styles.formField}>
					<SwitchWithLabel
						label="Confidential report"
						value={isConfidential}
						onValueChange={setIsConfidential}
						disabled={isSubmitting}
					/>
					{isConfidential ? (
						<Card tone="warning" style={styles.confidentialWarning}>
							<Text style={styles.confidentialWarningText}>
								{CONFIDENTIAL_EMPLOYEE_EVENT_SUBMISSION_HINT}
							</Text>
						</Card>
					) : null}
				</View>

				<View style={styles.formField}>
					<Text style={styles.formLabel}>Photos (optional)</Text>
					{pendingAssets.map((asset, index) => {
						const photoName = asset.fileName || `Photo ${index + 1}`;

						return (
							<View key={`${asset.uri}-${index}`} style={styles.attachmentRow}>
								<Feather name="image" size={18} color={colors.iconMuted} />
								<Text style={styles.attachmentName} numberOfLines={1}>
									{photoName}
								</Text>
								<Pressable
									onPress={() => removePhoto(index)}
									disabled={isSubmitting}
									accessibilityRole="button"
									accessibilityLabel={`Remove ${photoName}`}
									style={({ pressed }) => [styles.attachmentRemove, pressed && styles.pressed]}
								>
									<Feather name="x-circle" size={22} color={colors.iconMuted} />
								</Pressable>
							</View>
						);
					})}
					<SecondaryButton
						label="Add photo"
						icon="camera"
						onPress={addPhoto}
						disabled={isSubmitting}
						style={styles.addPhotoButton}
					/>
				</View>
			</View>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space, radius, touch } }) => ({
	form: {
		gap: space.lg,
	},
	formField: {
		gap: space.xs,
	},
	formLabel: {
		...type.fieldLabel,
		color: colors.text,
	},
	mutedText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	confidentialWarning: {
		marginTop: space.sm,
		padding: space.md,
	},
	confidentialWarningText: {
		...type.secondary,
		color: colors.warning,
	},
	attachmentRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
		minHeight: touch.min,
		paddingLeft: space.md,
		borderRadius: radius.card,
		backgroundColor: colors.card,
	},
	attachmentName: {
		...type.bodySmall,
		color: colors.text,
		flex: 1,
	},
	attachmentRemove: {
		width: touch.min,
		height: touch.min,
		alignItems: 'center',
		justifyContent: 'center',
	},
	pressed: {
		opacity: 0.7,
	},
	addPhotoButton: {
		alignSelf: 'flex-start',
		marginTop: space.xs,
	},
});

export default ReportIncidentView;
